/**
 * Gap-aware LTTB (Largest-Triangle-Three-Buckets) downsampling.
 *
 * The backend already caps each metric at ~1000 points, but a chart only a few
 * hundred pixels wide still overplots. This reduces a series to roughly the
 * chart's pixel width while preserving the visual shape (peaks/troughs).
 *
 * Mirrors the server-side implementation in
 * api-dsh/app/dsh/apis/services/air_stats_service/lttb.py — keep them in sync.
 * Gaps (null y) are preserved: each contiguous non-null run is downsampled
 * independently, with a single null marker kept between runs so the line breaks
 * instead of bridging across missing data.
 */
export interface XYPoint {
  x: number;
  y: number | null;
}

/** Standard LTTB over points whose `y` is never null. Keeps first and last. */
const lttbRun = (points: XYPoint[], threshold: number): XYPoint[] => {
  const n = points.length;
  if (threshold >= n || threshold < 3) return points.slice();

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y as number);

  const sampled: XYPoint[] = [points[0] as XYPoint];
  // Buckets span the n-2 interior points across threshold-2 selections.
  const every = (n - 2) / (threshold - 2);
  let a = 0; // index of the previously selected point

  for (let i = 0; i < threshold - 2; i++) {
    // Average point of the next bucket (the third triangle vertex).
    const avgStart = Math.floor((i + 1) * every) + 1;
    const avgEnd = Math.min(Math.floor((i + 2) * every) + 1, n);
    const avgRange = avgEnd - avgStart;
    let avgX: number;
    let avgY: number;
    if (avgRange <= 0) {
      avgX = xs[n - 1] as number;
      avgY = ys[n - 1] as number;
    } else {
      avgX = 0;
      avgY = 0;
      for (let j = avgStart; j < avgEnd; j++) {
        avgX += xs[j] as number;
        avgY += ys[j] as number;
      }
      avgX /= avgRange;
      avgY /= avgRange;
    }

    // Pick the point in the current bucket forming the largest triangle.
    const rangeStart = Math.floor(i * every) + 1;
    const rangeEnd = Math.min(Math.floor((i + 1) * every) + 1, n);
    const ax = xs[a] as number;
    const ay = ys[a] as number;
    let maxArea = -1;
    let maxIdx = rangeStart;
    for (let j = rangeStart; j < rangeEnd; j++) {
      const area = Math.abs(
        (ax - avgX) * ((ys[j] as number) - ay) - (ax - (xs[j] as number)) * (avgY - ay)
      );
      if (area > maxArea) {
        maxArea = area;
        maxIdx = j;
      }
    }

    sampled.push(points[maxIdx] as XYPoint);
    a = maxIdx;
  }

  sampled.push(points[n - 1] as XYPoint);
  return sampled;
};

/**
 * Indices LTTB keeps for a reference series (xs, ys). For multi-series charts
 * that share one x-axis: pick indices once from a reference metric, then slice
 * every series by them so they stay aligned. No gap handling (callers coerce
 * missing values to 0). Keeps first and last.
 */
export const lttbIndices = (xs: number[], ys: number[], threshold: number): number[] => {
  const n = xs.length;
  if (threshold >= n || threshold < 3) return xs.map((_, i) => i);

  const sampled = [0];
  const every = (n - 2) / (threshold - 2);
  let a = 0;

  for (let i = 0; i < threshold - 2; i++) {
    const avgStart = Math.floor((i + 1) * every) + 1;
    const avgEnd = Math.min(Math.floor((i + 2) * every) + 1, n);
    const avgRange = avgEnd - avgStart;
    let avgX: number;
    let avgY: number;
    if (avgRange <= 0) {
      avgX = xs[n - 1] as number;
      avgY = ys[n - 1] as number;
    } else {
      avgX = 0;
      avgY = 0;
      for (let j = avgStart; j < avgEnd; j++) {
        avgX += xs[j] as number;
        avgY += ys[j] as number;
      }
      avgX /= avgRange;
      avgY /= avgRange;
    }

    const rangeStart = Math.floor(i * every) + 1;
    const rangeEnd = Math.min(Math.floor((i + 1) * every) + 1, n);
    const ax = xs[a] as number;
    const ay = ys[a] as number;
    let maxArea = -1;
    let maxIdx = rangeStart;
    for (let j = rangeStart; j < rangeEnd; j++) {
      const area = Math.abs(
        (ax - avgX) * ((ys[j] as number) - ay) - (ax - (xs[j] as number)) * (avgY - ay)
      );
      if (area > maxArea) {
        maxArea = area;
        maxIdx = j;
      }
    }

    sampled.push(maxIdx);
    a = maxIdx;
  }

  sampled.push(n - 1);
  return sampled;
};

/**
 * Downsample to ~`threshold` points, preserving gaps. Returns the input
 * unchanged when it already fits.
 */
export const lttbGapAware = (points: XYPoint[], threshold: number): XYPoint[] => {
  const n = points.length;
  if (n <= threshold) return points;

  const nonNullTotal = points.reduce((c, p) => c + (p.y !== null ? 1 : 0), 0);
  if (nonNullTotal === 0) return [points[0] as XYPoint, points[n - 1] as XYPoint];

  const out: XYPoint[] = [];
  let i = 0;
  while (i < n) {
    if ((points[i] as XYPoint).y === null) {
      // Collapse a null run into a single gap marker.
      out.push(points[i] as XYPoint);
      i++;
      while (i < n && (points[i] as XYPoint).y === null) i++;
    } else {
      let j = i + 1;
      while (j < n && (points[j] as XYPoint).y !== null) j++;
      const run = points.slice(i, j);
      const budget = Math.max(2, Math.round((threshold * run.length) / nonNullTotal));
      out.push(...lttbRun(run, budget));
      i = j;
    }
  }
  return out;
};
